import { React, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { HACKER_PORTAL_PATH } from "../constants/routes.js";
import { importWithRetry } from "../lib/lazyWithRetry.js";
import {
  hasSessionFlag,
  readCachedRole,
  writeCachedRole,
  HAS_SESSION_KEY,
  SESSION_FLAG_EVENT,
} from "../lib/sessionFlag.js";

// No static firebase imports here: the Navbar is in the main bundle on every
// page, and auth + firestore are its heaviest dependencies. All firebase work
// lives in src/auth/navbarUser.js, dynamically imported after mount.
const loadNavbarAuth = () =>
  importWithRetry(
    () => import("../auth/navbarUser.js"),
    (mod) =>
      typeof mod?.watchNavbarUser === "function" && typeof mod?.signOutAndReload === "function",
  );

function signOutAndGo(navigate) {
  // A stale chunk here costs one page reload; the fresh page's Log Out works.
  // If the import still fails after that (offline), the session survives the
  // click — log it so telemetry sees the failed sign-out.
  loadNavbarAuth()
    .then(({ signOutAndReload }) => signOutAndReload())
    .catch((error) => console.error("Sign-out failed to load auth module:", error));
  if (navigate) navigate("/");
}

/** Synthwave-themed navigation link.
 * Default: light text. Hover: cyan with neon glow via text-shadow.
 * Pairs with `navLinkBar` for padding/sizing. */
const navLink =
  "inline-flex items-center justify-center text-sm font-semibold tracking-wide no-underline antialiased transition-colors duration-200 text-white/90 hover:text-[#00f0ff] hover:[text-shadow:0_0_8px_#00f0ff]";

const navLinkBar = "px-3 py-2";

/** Active-page styling: cyan text with a neon-glowing underline. The 3px
 *  stroke (up from 2px) keeps the current page findable at a glance. */
const navLinkActive =
  "text-[#00f0ff] underline decoration-[#00f0ff] decoration-[3px] underline-offset-[10px] [text-shadow:0_0_12px_#00f0ff]";

/** CTA pill: solid neon cyan with a soft glowing box-shadow.
 * Bold, no outline, reads as the primary action. */
const navRegister =
  "inline-flex items-center justify-center text-sm font-bold tracking-wide text-[#0C1440] no-underline antialiased rounded-full px-5 py-2 transition-all duration-200 bg-[#00f0ff] hover:bg-[#33f4ff] shadow-[0_0_20px_rgba(0,240,255,0.45)] hover:shadow-[0_0_28px_rgba(0,240,255,0.7)]";

/** Routes that render the navbar in document flow. Empty: every page uses the
 * `position: fixed` overlay navbar so it always pins to the viewport during scroll. */
export const NAVBAR_TOOL_ROUTES = new Set([]);

const Navbar = ({ layout = "fixed" }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const navbarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Read once, at mount: what this browser was last signed in as. Firebase
  // cannot answer that question for another two async hops (restoring the
  // session, then reading users/{uid}), and painting logged-out until then is
  // what makes the nav visibly flip on every page load for staff and hackers.
  const cachedRole = useMemo(() => readCachedRole(), []);
  const [authResolved, setAuthResolved] = useState(false);
  const [loggedIn, setLoggedIn] = useState(Boolean(cachedRole));

  // Before firebase answers, the cache stands in; after, it is ignored
  // entirely. Signed-out and still-loading both look like `user: null`, so
  // authResolved is what separates them — without it a signed-out visitor
  // would keep rendering a stale role forever.
  const shownSignedIn = authResolved ? Boolean(user) : Boolean(cachedRole);
  const shownRole = authResolved ? (userData?.role ?? null) : cachedRole;
  // Links that mean "signed in, and not one of these roles" need a role in
  // hand; a signed-in account with no user doc renders none of them, exactly
  // as it did when the checks read `user && userData && ...`.
  const hasRole = shownRole !== null;
  // A signed-in hacker navigates from the portal sidebar, which carries the
  // event links and more besides. Repeating a subset of them up here gave the
  // same person two different menus to the same weekend, so the top bar drops
  // to Home, the way in, and the way out. Staff, judges and sponsors keep the
  // marketing links: they have no portal sidebar to read them from.
  const isHacker =
    hasRole && !["staff", "judge", "sponsor"].includes(shownRole);


  useEffect(() => {
    let cancelled = false;
    let unsubscribe = () => {};
    const startAuthWatch = () =>
      loadNavbarAuth()
        .then(({ watchNavbarUser }) => {
          if (cancelled) return;
          unsubscribe = watchNavbarUser(({ user: nextUser, userData: nextData }) => {
            setAuthResolved(true);
            setUser(nextUser);
            setUserData(nextData);
            if (nextUser) setLoggedIn(true);
            // Seed the next page load. Sign-out clears this through
            // clearSessionFlag, so there is nothing to clear here.
            if (nextUser && nextData?.role) writeCachedRole(nextData.role);
          });
        })
        // Auth chunk unreachable after the retry reload: render the logged-out
        // navbar instead of crashing every page, but keep the error visible.
        .catch((error) => console.error("Navbar auth failed to load:", error));
    // Most visitors are anonymous, and for them the only thing this watcher
    // could ever say is "logged out" — which is already the default render.
    // Don't pay the firebase auth+firestore download to hear it. Load auth
    // only when the has-session flag hints a session exists, or the moment a
    // sign-in completes (the Navbar mounts once for the whole SPA, so a later
    // sign-in must poke this effect from outside). Two triggers, one shot:
    // the flag's custom event covers a sign-in in this tab, and the browser's
    // `storage` event covers a sign-in in another tab writing the flag —
    // before the gate, that other tab's firebase indexedDB sync handled it.
    const removeListeners = () => {
      window.removeEventListener(SESSION_FLAG_EVENT, onSessionFlag);
      window.removeEventListener("storage", onStorage);
    };
    const onSessionFlag = () => {
      removeListeners();
      startAuthWatch();
    };
    const onStorage = (event) => {
      if (event.key === HAS_SESSION_KEY && event.newValue !== null) onSessionFlag();
    };
    if (hasSessionFlag()) {
      startAuthWatch();
    } else {
      window.addEventListener(SESSION_FLAG_EVENT, onSessionFlag);
      window.addEventListener("storage", onStorage);
    }
    return () => {
      cancelled = true;
      removeListeners();
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (layout === "flow") {
        setScrolled(false);
        return;
      }

      const isScrolled = window.scrollY > 80;
      setScrolled(isScrolled);
    };

    // One rAF per frame: without the pending guard a trackpad flick queued a
    // callback per scroll event instead of per frame.
    let rafId = 0;
    const throttledScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        handleScroll();
      });
    };

    window.addEventListener("scroll", throttledScroll, { passive: true });

    handleScroll();

    return () => {
      window.removeEventListener("scroll", throttledScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [location.pathname, layout]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const ghostCls = `${navLink} ${navLinkBar}`;
  const registerCls = `${navRegister} ${navLinkBar}`;
  const mobileGhostCls = `${navLink} ${navLinkBar}`;
  const mobileRegisterCls = `${navRegister} ${navLinkBar}`;

  /** Returns nav link classes with active-state styling when the current
   * pathname matches `path`. Use for ghost-style page nav links. */
  const linkCls = (path, base = ghostCls) =>
    location.pathname === path
      ? `${base} ${navLinkActive}`
      : `${base} text-white`;

  return (
    <header
      id="main-navbar"
      className={`${
        layout === "flow" ? "flex-shrink-0 z-40 w-full" : "fixed z-50 top-0 left-0 right-0 w-full"
      } transition-all duration-300 ease-out ${
        scrolled || isMenuOpen
          ? "bg-[rgba(8,3,26,0.75)] backdrop-blur-[12px] backdrop-saturate-[150%] border-b border-[rgba(0,255,255,0.25)] shadow-[0_4px_24px_rgba(0,240,255,0.08)]"
          : "bg-transparent border-b border-transparent shadow-none"
      }`}
    >
      <div
        ref={navbarRef}
        id="main-navbar-inner"
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5"
        style={{
          fontFamily: '"Space Grotesk", system-ui, sans-serif',
        }}
      >
        <div
          className={`items-center ${
            layout === "flow"
              ? "flex justify-between"
              : scrolled
              ? "flex justify-center md:flex-col md:space-y-4 md:items-center"
              : "grid grid-cols-[1fr_auto_1fr] gap-x-2"
          }`}
        >
          {/* mobile hamburger menu */}
          <div className={`flex items-center gap-3 min-w-0 ${scrolled && layout !== "flow" ? "" : "justify-self-start"}`}>
            <button
              className="text-white md:hidden flex-shrink-0 transition-colors duration-200 hover:text-cyan-400 hover:[text-shadow:0_0_8px_rgba(34,211,238,0.6)] cursor-pointer"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    isMenuOpen
                      ? "M6 18L18 6M6 6l12 12"
                      : "M4 6h16M4 12h16M4 18h16"
                  }
                />
              </svg>
            </button>

            {/* Portal target: page-specific left content (e.g. "Application Review") */}
            <div id="navbar-portal-left" className="hidden" />
          </div>

          {/* desktop nav, centered in the middle column so left/right widths don't shift it */}
          <div className="hidden md:flex items-center justify-center gap-x-6 gap-y-2 flex-wrap">
            {shownRole === "judge" ? (
              <>
                <Link to="/judging" className={linkCls("/judging")}>
                  Judging
                </Link>
              </>
            ) : (
              <>
                <Link to="/" className={linkCls("/")}>
                  Home
                </Link>
                {!isHacker && (
                  <>
                    <Link to="/tracks" className={linkCls("/tracks")}>
                      Tracks
                    </Link>
                    <Link to="/schedule" className={linkCls("/schedule")}>
                      Schedule
                    </Link>
                    <Link to="/sponsors" className={linkCls("/sponsors")}>
                      Sponsors
                    </Link>
                  </>
                )}

                {/* Role-gated app links sit inline with the rest */}
                {shownRole === "staff" && (
                  <>
                    <Link to="/staff/dashboard" className={linkCls("/staff/dashboard")}>
                      Staff Dashboard
                    </Link>
                    {/* Staff get the portal link too — they have portal access
                        for debugging (see HackerDashboardLayout). */}
                    <Link to={HACKER_PORTAL_PATH} className={linkCls(HACKER_PORTAL_PATH)}>
                      Hacker Portal
                    </Link>
                    <Link to="/judging" className={linkCls("/judging")}>
                      Judging
                    </Link>
                  </>
                )}
                {/* Account actions stay inline in the same centered row */}
                {!loggedIn && (
                  <>
                    <Link to="/login" className={linkCls("/login")}>
                      Login
                    </Link>
                    <Link to="/apply" className={registerCls}>
                      Apply
                    </Link>
                  </>
                )}
                {hasRole && shownRole !== "staff" && (
                  <Link to={HACKER_PORTAL_PATH} className={linkCls(HACKER_PORTAL_PATH)}>
                    Hacker Portal
                  </Link>
                )}
              </>
            )}
            {loggedIn && (
              <button
                type="button"
                onClick={() => signOutAndGo(navigate)}
                // hover:underline matches the global a:hover rule the real
                // links get — this is a <button>, so it needs it explicitly.
                className={`${navLink} ${navLinkBar} text-white hover:underline`}
              >
                Log Out
              </button>
            )}
          </div>

          {/* Portal target: page-specific right content (e.g. export buttons) */}
          <div
            id="navbar-portal-right"
            className={`hidden ${scrolled && layout !== "flow" ? "" : "justify-self-end"}`}
          />
        </div>

        {/* mobile nav, slides in from top with frosted-glass parent */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-out ${
            isMenuOpen
              ? "max-h-[80vh] opacity-100 mt-3 pt-3 border-t border-cyan-400/20"
              : "max-h-0 opacity-0 pointer-events-none"
          }`}
        >
          <div className="flex flex-col space-y-2">
              {shownRole === "judge" ? (
                <>
                  <Link
                    to="/judging"
                    className={linkCls("/judging", mobileGhostCls)}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Judging
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/"
                    className={linkCls("/", mobileGhostCls)}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Home
                  </Link>

                  {!isHacker && (
                    <>

                      <Link
                        to="/tracks"
                        className={linkCls("/tracks", mobileGhostCls)}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Tracks
                      </Link>

                      <Link
                        to="/schedule"
                        className={linkCls("/schedule", mobileGhostCls)}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Schedule
                      </Link>

                      <Link
                        to="/sponsors"
                        className={linkCls("/sponsors", mobileGhostCls)}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Sponsors
                      </Link>
                    </>
                  )}

                  {/* Mobile Login link for non-authenticated users */}
                  {!shownSignedIn && (
                    <Link
                      to="/login"
                      className={linkCls("/login", mobileGhostCls)}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Login
                    </Link>
                  )}

                  {!loggedIn && (
                    <Link
                      to="/apply"
                      className={mobileRegisterCls}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Apply
                    </Link>
                  )}

                  {/* Mobile My Application / Hacker Portal */}
                  {hasRole && shownRole !== "sponsor" && shownRole !== "staff" && (
                    <Link
                      to={HACKER_PORTAL_PATH}
                      className={linkCls(HACKER_PORTAL_PATH, mobileGhostCls)}
                      onClick={() => setIsMenuOpen(false)}
                    >
                       Hacker Portal
                    </Link>
                  )}


                  {/* Mobile Staff Dashboard link for staff users */}
                  {shownRole === "staff" && (
                    <>
                      <Link
                        to="/staff/dashboard"
                        className={linkCls("/staff/dashboard", mobileGhostCls)}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Staff Dashboard
                      </Link>
                      <Link
                        to={HACKER_PORTAL_PATH}
                        className={linkCls(HACKER_PORTAL_PATH, mobileGhostCls)}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Hacker Portal
                      </Link>
                      <Link
                        to="/judging"
                        className={linkCls("/judging", mobileGhostCls)}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Judging
                      </Link>
                    </>
                  )}

                  {/* Log Out stays last: it's the exit, not a destination. */}
                  {loggedIn && (
                    <button
                      type="button"
                      onClick={() => signOutAndGo()}
                      className={`${mobileGhostCls} text-white hover:underline`}
                    >
                      Log Out
                    </button>
                  )}
                </>
              )}


            </div>
          </div>
        </div>
    </header>
  );
};

export default Navbar;
